/**
 * useCalendarSync — Hook that manages calendar sync polling lifecycle.
 *
 * Starts the 5-minute polling cycle for connected external calendars
 * when the app is online, and stops polling when offline. Should be
 * mounted once in the AppShell so polling persists across module switches.
 *
 * @see Requirements 6.3, 6.4, 6.5, 6.6, 17.3
 */

import { useEffect } from 'react';
import { calendarSyncService } from '../services/calendarSyncService';

/**
 * Starts calendar sync polling when the app has network connectivity.
 * Stops polling when the app goes offline.
 * Call this once in a top-level component (e.g., AppShell).
 */
export function useCalendarSync(): void {
  useEffect(() => {
    function handleOnline() {
      calendarSyncService.startPolling();
    }

    function handleOffline() {
      calendarSyncService.stopPolling();
    }

    // Start polling if already online
    if (navigator.onLine) {
      calendarSyncService.startPolling();
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      calendarSyncService.stopPolling();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
}
